import type { FC } from 'react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { cssMap } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import type { AiSuggestionsRightRailEntryPoint } from '@atlaskit/editor-common/analytics';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/useSharedPluginStateWithSelector';

import type { Suggestion } from '@atlassian/editor-plugin-ai-suggestions';

import {
	useFullPageEditorApiState,
	type FullPageEditorPresetAPI,
} from '@confluence/full-page-editor-store';

import { useSuggestionsPanelOpenAnalytics } from '../analytics.ts';
import { SuggestionsEmptyState } from './SuggestionsEmptyState.tsx';
import { SuggestionsInlineLoadingBar } from './SuggestionsInlineLoadingBar.tsx';
import { SuggestionsLoadingState } from './SuggestionsLoadingState.tsx';
import { SuggestionItem } from './SuggestionItem.tsx';
type AISuggestionsSharedState = ReturnType<
	NonNullable<FullPageEditorPresetAPI['aiSuggestions']>['sharedState']['currentState']
>;

const getOrderedSuggestions = (state: { aiSuggestionsState?: AISuggestionsSharedState }) => {
	const aiSuggestionsState = state?.aiSuggestionsState;
	if (!aiSuggestionsState) {
		return undefined;
	}
	const result: NonNullable<ReturnType<(typeof aiSuggestionsState)['suggestionById']['get']>>[] =
		[];
	for (const id of aiSuggestionsState.orderedSuggestionIds) {
		const suggestion = aiSuggestionsState.suggestionById.get(id);
		if (suggestion !== undefined) {
			result.push(suggestion);
		}
	}
	return result;
};

const styles = cssMap({
	panelContainer: {
		display: 'flex',
		flexDirection: 'column',
		paddingBlock: token('space.100'),
		paddingInline: token('space.250'),
		gap: token('space.200'),
		height: '100%',
		overflow: 'auto',
	},
	emptyState: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		paddingBlock: token('space.600'),
		gap: token('space.100'),
		textAlign: 'center',
	},
});

export const SuggestionsPanel: FC<{ entryPoint?: AiSuggestionsRightRailEntryPoint }> = ({
	entryPoint = 'suggestionsTab',
}) => {
	const { fullPageEditorPresetAPI } = useFullPageEditorApiState();

	const {
		suggestions,
		selectedSuggestionId,
		thinkingStatus,
		thinkingStartedAt,
		thinkingProgressTarget,
		regeneratingSuggestionIds,
	} = useSharedPluginStateWithSelector(
		fullPageEditorPresetAPI,
		['aiSuggestions'],
		(state: { aiSuggestionsState?: AISuggestionsSharedState }) => {
			return {
				suggestions: getOrderedSuggestions(state),
				selectedSuggestionId: state?.aiSuggestionsState?.selectedSuggestion,
				thinkingStatus: state?.aiSuggestionsState?.thinkingStatus,
				// Shared thinking-session anchor so the inline bar stays in sync with
				// the editor's thinking bar across mount/unmount.
				thinkingStartedAt: state?.aiSuggestionsState?.thinkingStartedAt ?? null,
				thinkingProgressTarget: state?.aiSuggestionsState?.thinkingProgressTarget ?? null,
				// Returned as the Set itself rather than a derived array — the hook
				// compares selector results shallowly, and this identity only changes
				// when the plugin swaps the set.
				regeneratingSuggestionIds: state?.aiSuggestionsState?.regeneratingSuggestionIds,
			};
		},
	);
	/**
	 * Bring the list up to date whenever the panel opens — this component is only
	 * rendered while the Suggestions tab is active, so a mount *is* the tab being
	 * opened (and covers the sidebar being opened straight onto this tab, which a
	 * tab-change listener would miss).
	 *
	 * Suggestions already in state get their staleness re-checked after however
	 * much editing happened while the panel was closed. An empty panel remains idle
	 * until the user explicitly starts Review from its empty state.
	 */
	useEffect(() => {
		// Keyed on the api rather than mount-only because it can be absent on the
		// first render if the editor isn't ready yet. It's a stable reference for the
		// life of an editor instance, so this runs once per open.
		const aiSuggestions = fullPageEditorPresetAPI?.aiSuggestions;
		if (!aiSuggestions) {
			return;
		}

		// Read through the api rather than the debounced hook state, which can still
		// hold a value from before the panel opened.
		const state = aiSuggestions.sharedState.currentState();

		// A generation is already running (e.g. started from the sidebar control
		// that opened this panel) — it refreshes the list when it lands.
		if (state?.thinkingStatus === 'thinking') {
			return;
		}

		if ((state?.orderedSuggestionIds.length ?? 0) === 0) {
			return;
		}

		// Regenerate-only: a full refresh would re-fetch, which reorders the list and
		// can resurrect suggestions whose accept/discard status update is in flight.
		aiSuggestions.actions.refreshSuggestions({ regenerateOnly: true });
	}, [fullPageEditorPresetAPI]);

	const hasSuggestions = (suggestions?.length ?? 0) > 0;
	const shouldFireEmptyStateExposureRef = useRef(!hasSuggestions);
	const hasRenderedLoadingStateRef = useRef(false);
	const [shouldShowNoSuggestionsResult, setShouldShowNoSuggestionsResult] = useState(false);

	// Latch for the inline loading bar. `thinkingStatus` can flip
	// 'thinking' -> 'complete' in one React commit batch, so we latch on when
	// thinking starts and release once the regenerated suggestions arrive.
	const [isRegenerating, setIsRegenerating] = useState(false);
	const suggestionIdsKey = (suggestions ?? []).map((s: Suggestion) => s?.id).join(',');
	const suggestionIdsKeyAtRegenStartRef = useRef<string | null>(null);

	useEffect(() => {
		// Track that the loading state was shown so a later empty result shows the
		// "no suggestions" state (runs even when previous suggestions remain).
		if (thinkingStatus === 'thinking') {
			hasRenderedLoadingStateRef.current = true;
			setShouldShowNoSuggestionsResult(false);
			return;
		}

		if (hasSuggestions) {
			shouldFireEmptyStateExposureRef.current = false;
			setShouldShowNoSuggestionsResult(false);
			return;
		}

		if (thinkingStatus === 'complete' && hasRenderedLoadingStateRef.current) {
			setShouldShowNoSuggestionsResult(true);
		}
	}, [hasSuggestions, thinkingStatus]);

	// Manage the inline loading-bar latch (see comment above).
	useEffect(() => {
		if (thinkingStatus === 'thinking') {
			// Remember the current set so we can detect when new results arrive.
			suggestionIdsKeyAtRegenStartRef.current = suggestionIdsKey;
			setIsRegenerating(true);
			return;
		}

		if (thinkingStatus === 'idle') {
			// Cancelled / reset.
			suggestionIdsKeyAtRegenStartRef.current = null;
			setIsRegenerating(false);
			return;
		}

		// 'complete': keep the bar until new suggestions arrive (id set changes),
		// with a safety timeout so it can't get stuck if the set is unchanged/empty.
		if (!isRegenerating) {
			return;
		}

		if (suggestionIdsKey !== suggestionIdsKeyAtRegenStartRef.current) {
			suggestionIdsKeyAtRegenStartRef.current = null;
			setIsRegenerating(false);
			return;
		}

		const timeoutId = setTimeout(() => {
			suggestionIdsKeyAtRegenStartRef.current = null;
			setIsRegenerating(false);
		}, 500);
		return () => clearTimeout(timeoutId);
	}, [thinkingStatus, suggestionIdsKey, isRegenerating]);

	const handleSelect = useCallback(
		(id: string) => {
			const isDeselecting = selectedSuggestionId === id;
			fullPageEditorPresetAPI?.aiSuggestions?.actions.selectSuggestion(isDeselecting ? null : id, {
				interactionPoint: 'sidebar',
			});
		},
		[fullPageEditorPresetAPI, selectedSuggestionId],
	);

	const handleAccept = useCallback(
		(id: string) => {
			fullPageEditorPresetAPI?.aiSuggestions?.actions.acceptSuggestion(id);
		},
		[fullPageEditorPresetAPI],
	);

	const handleDiscard = useCallback(
		(id: string) => {
			fullPageEditorPresetAPI?.aiSuggestions?.actions.discardSuggestion(id);
		},
		[fullPageEditorPresetAPI],
	);

	const handleDismiss = useCallback(
		(id: string) => {
			fullPageEditorPresetAPI?.aiSuggestions?.actions.discardSuggestion(id);
		},
		[fullPageEditorPresetAPI],
	);

	const handleGoodSuggestion = useCallback(
		(id: string) => {
			fullPageEditorPresetAPI?.aiSuggestions?.actions.submitSuggestionFeedback(id, 'good', {
				interactionPoint: 'sidebar',
			});
		},
		[fullPageEditorPresetAPI],
	);

	const handleBadSuggestion = useCallback(
		(id: string) => {
			fullPageEditorPresetAPI?.aiSuggestions?.actions.submitSuggestionFeedback(id, 'bad', {
				interactionPoint: 'sidebar',
			});
		},
		[fullPageEditorPresetAPI],
	);

	const handleToggleDeletedDiffs = useCallback(
		(id: string, hideDeletedDiffs: boolean) => {
			fullPageEditorPresetAPI?.aiSuggestions?.actions.selectSuggestion(id, {
				interactionPoint: 'sidebar',
				hideDeletedDiffs,
				scrollIntoView: false,
			});
		},
		[fullPageEditorPresetAPI],
	);

	const isThinking = thinkingStatus === 'thinking';
	const showInlineLoadingBar = isThinking || isRegenerating;
	const isFullPanelLoading = !hasSuggestions && isThinking;

	useSuggestionsPanelOpenAnalytics({
		entryPoint,
		isFullPanelLoading,
		suggestions,
	});

	if (!suggestions?.length) {
		if (isFullPanelLoading) {
			return <SuggestionsLoadingState api={fullPageEditorPresetAPI} />;
		}

		return (
			<SuggestionsEmptyState
				api={fullPageEditorPresetAPI}
				shouldFireExposureAnalytics={shouldFireEmptyStateExposureRef.current}
				variant={shouldShowNoSuggestionsResult ? 'noSuggestionsResult' : 'initial'}
			/>
		);
	}

	// Suggestions exist: keep the list visible; show the inline bar at the bottom
	// while regenerating.
	return (
		<div css={styles.panelContainer} data-testid="suggestions-panel-list-container">
			{suggestions.map((suggestion: Suggestion) => (
				<SuggestionItem
					key={suggestion.id}
					suggestion={suggestion}
					isSelected={selectedSuggestionId === suggestion.id}
					isRegenerating={regeneratingSuggestionIds?.has(suggestion.id) ?? false}
					onSelect={handleSelect}
					onAccept={handleAccept}
					onDiscard={handleDiscard}
					onDismiss={handleDismiss}
					onGoodSuggestion={handleGoodSuggestion}
					onBadSuggestion={handleBadSuggestion}
					onToggleDeletedDiffs={handleToggleDeletedDiffs}
				/>
			))}
			{showInlineLoadingBar && (
				<SuggestionsInlineLoadingBar
					api={fullPageEditorPresetAPI}
					startedAt={thinkingStartedAt}
					progressTarget={thinkingProgressTarget}
				/>
			)}
		</div>
	);
};
